import React, { useState } from "react";
import { StorageService } from "../../services/storage.js";
import { Plus, Edit3, Trash2, X, Clock } from "lucide-react";

export const AdminCurriculum = ({
  curriculum,
  currentUser,
  onCurriculumUpdated
}) => {
  const [editingItem, setEditingItem] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const canEdit = currentUser.role === "super_admin" || currentUser.role === "principal" || currentUser.role === "teacher" || currentUser.role === "editor";

  const handleStartCreate = () => {
    setEditingItem({
      class_name: "",
      class_name_bn: "",
      age_group: "5 - 10 Years",
      duration: "1 Year",
      description: "",
      description_bn: "",
      subjects: ["Bangla", "Arabic", "English", "Mathematics"],
      subjects_bn: ["বাংলা", "আরবি", "ইংরেজি", "গণিত"],
      features: ["Classroom mentorship", "Daily homework review"],
      schedule: "8:00 AM - 1:00 PM",
      is_active: true
    });
    setIsCreating(true);
  };

  const handleStartEdit = (item) => {
    setEditingItem({ ...item });
    setIsCreating(false);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!editingItem || !editingItem.class_name) return;
    StorageService.saveCurriculumItem(editingItem);
    setEditingItem(null);
    setIsCreating(false);
    onCurriculumUpdated();
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this curriculum module?")) {
      StorageService.deleteCurriculumItem(id);
      onCurriculumUpdated();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
            Curriculum & Classes Manager
          </h1>
          <p className="text-xs text-slate-500">
            Configure dual-stream syllabus subjects, age brackets, and academic timings
          </p>
        </div>

        {canEdit && (
          <button
            onClick={handleStartCreate}
            className="px-4 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-sm transition-all flex items-center gap-1.5 cursor-pointer self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" />
            <span>Add Class / Department</span>
          </button>
        )}
      </div>

      {editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-xl overflow-y-auto border border-stone-200">
            <form onSubmit={handleSave} className="p-6 sm:p-8 space-y-5">
              <div className="flex items-center justify-between border-b border-stone-200 pb-3">
                <h2 className="text-base font-bold text-slate-900">
                  {isCreating ? "Add New Curriculum Class" : `Edit: ${editingItem.class_name}`}
                </h2>
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-700 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Class / Dept Name (English) *
                  </label>
                  <input
                    type="text"
                    required
                    value={editingItem.class_name || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, class_name: e.target.value })}
                    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    শ্রেণি বা বিভাগের নাম (বাংলা) *
                  </label>
                  <input
                    type="text"
                    required
                    value={editingItem.class_name_bn || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, class_name_bn: e.target.value })}
                    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 font-bengali"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Age Group
                  </label>
                  <input
                    type="text"
                    value={editingItem.age_group || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, age_group: e.target.value })}
                    placeholder="4 - 6 Years"
                    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Course Duration
                  </label>
                  <input
                    type="text"
                    value={editingItem.duration || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, duration: e.target.value })}
                    placeholder="1 Year"
                    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Daily Schedule
                  </label>
                  <input
                    type="text"
                    value={editingItem.schedule || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, schedule: e.target.value })}
                    placeholder="8:00 AM - 12:30 PM"
                    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Description (English)
                  </label>
                  <textarea
                    rows={3}
                    value={editingItem.description || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    বিবরণ (বাংলা)
                  </label>
                  <textarea
                    rows={3}
                    value={editingItem.description_bn || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, description_bn: e.target.value })}
                    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 font-bengali"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-stone-200">
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-stone-100 rounded-lg cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-sm cursor-pointer"
                >
                  Save Curriculum Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {curriculum.map((item) => (
          <div
            key={item.id}
            className="bg-white p-6 rounded-2xl border border-stone-200 shadow-xs space-y-4 relative"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="text-[11px] font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded">
                  {item.age_group} · {item.duration}
                </span>
                <h3 className="text-base font-bold text-slate-900 mt-2 font-bengali">
                  {item.class_name_bn}
                </h3>
                <span className="text-xs text-slate-500 font-sans block">{item.class_name}</span>
              </div>

              <div className="flex items-center gap-1">
                {canEdit && (
                  <button
                    onClick={() => handleStartEdit(item)}
                    className="p-1.5 text-slate-500 hover:text-emerald-800 hover:bg-stone-100 rounded-lg cursor-pointer"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                )}
                {currentUser.role === "super_admin" && (
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded-lg cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed font-bengali">
              {item.description_bn}
            </p>

            <div className="pt-2 border-t border-stone-100">
              <span className="text-[11px] font-bold text-slate-700 block mb-1.5">
                পাঠ্য বিষয়সমূহ (Subjects):
              </span>
              <div className="flex flex-wrap gap-1.5">
                {item.subjects_bn.map((sub, i) => (
                  <span
                    key={i}
                    className="text-[11px] bg-stone-100 text-slate-800 px-2 py-0.5 rounded font-bengali"
                  >
                    {sub}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              <span>{item.schedule}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
